import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  doc,
  updateDoc,
  Timestamp
} from 'firebase/firestore';
import { toast } from 'react-toastify';
import Loader from './Loader';
import './Admin.css';

function Admin() {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();

  const [records, setRecords] = useState([]);
  const [filteredRecords, setFilteredRecords] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState('all');
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('attendance');
  const [timeLogs, setTimeLogs] = useState([]);
  const [filteredTimeLogs, setFilteredTimeLogs] = useState([]);
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [filteredLeaveRequests, setFilteredLeaveRequests] = useState([]);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectingLeave, setRejectingLeave] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');

  // Fetch all attendance records
  useEffect(() => {
    const q = query(
      collection(db, 'attendance'),
      orderBy('date', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const allRecords = [];
      const userSet = new Set();

      snapshot.forEach((doc) => {
        const data = { id: doc.id, ...doc.data() };
        allRecords.push(data);
        userSet.add(JSON.stringify({ id: data.userId, name: data.userName }));
      });

      setRecords(allRecords);
      setFilteredRecords(allRecords);

      // Extract unique users
      const uniqueUsers = Array.from(userSet).map((u) => JSON.parse(u));
      setUsers(uniqueUsers);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Fetch all time logs
  useEffect(() => {
    const q = query(
      collection(db, 'timelogs'),
      orderBy('date', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const allLogs = [];
      snapshot.forEach((docSnap) => {
        allLogs.push({ id: docSnap.id, ...docSnap.data() });
      });
      setTimeLogs(allLogs);
      setFilteredTimeLogs(allLogs);
    });

    return unsubscribe;
  }, []);

  // Fetch all leave requests
  useEffect(() => {
    const q = query(
      collection(db, 'leaves'),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const allLeaves = [];
      snapshot.forEach((docSnap) => {
        allLeaves.push({ id: docSnap.id, ...docSnap.data() });
      });
      setLeaveRequests(allLeaves);
      setFilteredLeaveRequests(allLeaves);
    });

    return unsubscribe;
  }, []);

  // Filter records when user selection changes
  useEffect(() => {
    if (selectedUser === 'all') {
      setFilteredRecords(records);
      setFilteredTimeLogs(timeLogs);
      setFilteredLeaveRequests(leaveRequests);
    } else {
      setFilteredRecords(records.filter((r) => r.userId === selectedUser));
      setFilteredTimeLogs(timeLogs.filter((l) => l.userId === selectedUser));
      setFilteredLeaveRequests(leaveRequests.filter((l) => l.userId === selectedUser));
    }
  }, [selectedUser, records, timeLogs, leaveRequests]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleApproveTimeLog = async (logId) => {
    try {
      await updateDoc(doc(db, 'timelogs', logId), {
        status: 'approved'
      });
      toast.success('Time log approved successfully!');
    } catch (error) {
      console.error('Error approving time log:', error);
      toast.error('Failed to approve time log. Please try again.');
    }
  };

  const handleRejectTimeLog = async (logId) => {
    try {
      await updateDoc(doc(db, 'timelogs', logId), {
        status: 'rejected'
      });
      toast.success('Time log rejected successfully!');
    } catch (error) {
      console.error('Error rejecting time log:', error);
      toast.error('Failed to reject time log. Please try again.');
    }
  };

  // Leave request handlers
  const handleApproveLeave = async (leaveId) => {
    try {
      await updateDoc(doc(db, 'leaves', leaveId), {
        status: 'approved',
        approvedAt: Timestamp.now(),
        approvedBy: currentUser.email
      });
      toast.success('Leave request approved successfully!');
    } catch (error) {
      console.error('Error approving leave request:', error);
      toast.error('Failed to approve leave request. Please try again.');
    }
  };

  const openRejectModal = (leave) => {
    setRejectingLeave(leave);
    setRejectionReason('');
    setShowRejectModal(true);
  };

  const handleRejectLeave = async () => {
    if (!rejectionReason.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }

    try {
      await updateDoc(doc(db, 'leaves', rejectingLeave.id), {
        status: 'rejected',
        rejectionReason: rejectionReason.trim(),
        rejectedAt: Timestamp.now(),
        rejectedBy: currentUser.email
      });
      toast.success('Leave request rejected successfully!');
      setShowRejectModal(false);
      setRejectingLeave(null);
      setRejectionReason('');
    } catch (error) {
      console.error('Error rejecting leave request:', error);
      toast.error('Failed to reject leave request. Please try again.');
    }
  };

  const getLeaveTypeLabel = (type) => {
    switch (type) {
      case 'sick':
        return 'Sick Leave';
      case 'emergency':
        return 'Emergency Leave';
      default:
        return 'Casual Leave';
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '--:--';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const calculateTotalBreakTime = (breaks) => {
    if (!breaks?.length) return '0m';
    let totalMs = 0;
    breaks.forEach((brk) => {
      if (brk.breakIn && brk.breakOut) {
        const breakIn = brk.breakIn.toDate ? brk.breakIn.toDate() : new Date(brk.breakIn);
        const breakOut = brk.breakOut.toDate ? brk.breakOut.toDate() : new Date(brk.breakOut);
        totalMs += breakOut - breakIn;
      }
    });
    const minutes = Math.round(totalMs / 60000);
    if (minutes >= 60) {
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      return `${hours}h ${mins}m`;
    }
    return `${minutes}m`;
  };

  const calculateWorkedHours = (record) => {
    if (!record.clockIn || !record.clockOut) return '--';

    const clockIn = record.clockIn.toDate ? record.clockIn.toDate() : new Date(record.clockIn);
    const clockOut = record.clockOut.toDate ? record.clockOut.toDate() : new Date(record.clockOut);

    let totalMs = clockOut - clockIn;

    // Subtract break time
    if (record.breaks?.length) {
      record.breaks.forEach((brk) => {
        if (brk.breakIn && brk.breakOut) {
          const breakIn = brk.breakIn.toDate ? brk.breakIn.toDate() : new Date(brk.breakIn);
          const breakOut = brk.breakOut.toDate ? brk.breakOut.toDate() : new Date(brk.breakOut);
          totalMs -= (breakOut - breakIn);
        }
      });
    }

    const hours = totalMs / (1000 * 60 * 60);
    return hours.toFixed(1) + 'h';
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return <span className="status-badge approved">Approved</span>;
      case 'rejected':
        return <span className="status-badge rejected">Rejected</span>;
      default:
        return <span className="status-badge pending">Pending</span>;
    }
  };

  if (loading) {
    return <Loader message="Loading admin panel..." />;
  }

  return (
    <div className="admin-container">
      <header className="admin-header">
        <div className="admin-header-left">
          <img
            src="https://www.alphawavesoftware.com/_next/static/media/logo-dark.128a83bb.png"
            alt="AlphaWave Software"
            className="admin-logo"
          />
        </div>
        <div className="header-right">
          <button className="logout-button" onClick={handleLogout}>
            Logout
          </button>
        </div>
      </header>

      {/* Tabs */}
      <div className="admin-tabs">
        <button
          className={`tab-btn ${activeTab === 'attendance' ? 'active' : ''}`}
          onClick={() => setActiveTab('attendance')}
        >
          Attendance Records
        </button>
        <button
          className={`tab-btn ${activeTab === 'timelogs' ? 'active' : ''}`}
          onClick={() => setActiveTab('timelogs')}
        >
          Time Logs
          {timeLogs.filter(l => l.status === 'pending').length > 0 && (
            <span className="pending-count">
              {timeLogs.filter(l => l.status === 'pending').length}
            </span>
          )}
        </button>
        <button
          className={`tab-btn ${activeTab === 'leaves' ? 'active' : ''}`}
          onClick={() => setActiveTab('leaves')}
        >
          Leave Requests
          {leaveRequests.filter(l => l.status === 'pending').length > 0 && (
            <span className="pending-count">
              {leaveRequests.filter(l => l.status === 'pending').length}
            </span>
          )}
        </button>
      </div>

      <div className="filter-section">
        <label>Filter by User:</label>
        <select
          value={selectedUser}
          onChange={(e) => setSelectedUser(e.target.value)}
        >
          <option value="all">All Users</option>
          {users.map((user) => (
            <option key={user.id} value={user.id}>
              {user.name}
            </option>
          ))}
        </select>
        <span className="record-count">
          {activeTab === 'attendance'
            ? `Showing ${filteredRecords.length} of ${records.length} records`
            : activeTab === 'timelogs'
            ? `Showing ${filteredTimeLogs.length} of ${timeLogs.length} time logs`
            : `Showing ${filteredLeaveRequests.length} of ${leaveRequests.length} leave requests`
          }
        </span>
      </div>

      {/* Attendance Records Table */}
      {activeTab === 'attendance' && (
        <div className="table-container">
          <table className="attendance-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>User</th>
                <th>Clock In</th>
                <th>Clock In Location</th>
                <th>Clock Out</th>
                <th>Clock Out Location</th>
                <th>Breaks</th>
                <th>Total Break</th>
                <th>Worked Hours</th>
                <th>Early Clock Out Reason</th>
              </tr>
            </thead>
            <tbody>
              {filteredRecords.length === 0 ? (
                <tr>
                  <td colSpan="10" className="no-records">No records found</td>
                </tr>
              ) : (
                filteredRecords.map((record) => (
                  <tr key={record.id}>
                    <td>{formatDate(record.date)}</td>
                    <td>{record.userName}</td>
                    <td>{formatTime(record.clockIn)}</td>
                    <td>
                      {record.clockInLocation ? (
                        <div className="location-cell">
                          <a
                            href={`https://www.google.com/maps?q=${record.clockInLocation.latitude},${record.clockInLocation.longitude}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="location-link"
                            title={`Coordinates: ${record.clockInLocation.latitude.toFixed(6)}, ${record.clockInLocation.longitude.toFixed(6)}`}
                          >
                            {record.clockInLocation.address || `${record.clockInLocation.latitude.toFixed(4)}, ${record.clockInLocation.longitude.toFixed(4)}`}
                          </a>
                          {record.clockInLocation.accuracy && (
                            <span className={`accuracy-badge ${record.clockInLocation.accuracy <= 50 ? 'good' : record.clockInLocation.accuracy <= 100 ? 'medium' : 'poor'}`}>
                              {record.clockInLocation.accuracy <= 50 ? 'Accurate' : record.clockInLocation.accuracy <= 100 ? '~' + record.clockInLocation.accuracy + 'm' : 'Low accuracy'}
                            </span>
                          )}
                        </div>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td>{formatTime(record.clockOut)}</td>
                    <td>
                      {record.clockOutLocation ? (
                        <div className="location-cell">
                          <a
                            href={`https://www.google.com/maps?q=${record.clockOutLocation.latitude},${record.clockOutLocation.longitude}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="location-link"
                            title={`Coordinates: ${record.clockOutLocation.latitude.toFixed(6)}, ${record.clockOutLocation.longitude.toFixed(6)}`}
                          >
                            {record.clockOutLocation.address || `${record.clockOutLocation.latitude.toFixed(4)}, ${record.clockOutLocation.longitude.toFixed(4)}`}
                          </a>
                          {record.clockOutLocation.accuracy && (
                            <span className={`accuracy-badge ${record.clockOutLocation.accuracy <= 50 ? 'good' : record.clockOutLocation.accuracy <= 100 ? 'medium' : 'poor'}`}>
                              {record.clockOutLocation.accuracy <= 50 ? 'Accurate' : record.clockOutLocation.accuracy <= 100 ? '~' + record.clockOutLocation.accuracy + 'm' : 'Low accuracy'}
                            </span>
                          )}
                        </div>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td>
                      {record.breaks?.length > 0 ? (
                        <div className="breaks-cell">
                          {record.breaks.map((brk, idx) => (
                            <div key={idx} className="break-entry">
                              {formatTime(brk.breakIn)} - {formatTime(brk.breakOut)}
                            </div>
                          ))}
                        </div>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td>{calculateTotalBreakTime(record.breaks)}</td>
                    <td>{calculateWorkedHours(record)}</td>
                    <td>
                      {record.earlyClockOutReason ? (
                        <span className="reason-badge">{record.earlyClockOutReason}</span>
                      ) : (
                        '-'
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Time Logs Table */}
      {activeTab === 'timelogs' && (
        <div className="table-container">
          <table className="attendance-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>User</th>
                <th>Hours</th>
                <th>Description</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredTimeLogs.length === 0 ? (
                <tr>
                  <td colSpan="6" className="no-records">No time logs found</td>
                </tr>
              ) : (
                filteredTimeLogs.map((log) => (
                  <tr key={log.id}>
                    <td>{formatDate(log.date)}</td>
                    <td>{log.userName}</td>
                    <td><strong>{log.hours}h</strong></td>
                    <td className="desc-cell">{log.description || '-'}</td>
                    <td>{getStatusBadge(log.status)}</td>
                    <td>
                      {log.status === 'pending' ? (
                        <div className="action-buttons">
                          <button
                            className="btn-approve"
                            onClick={() => handleApproveTimeLog(log.id)}
                          >
                            Approve
                          </button>
                          <button
                            className="btn-reject"
                            onClick={() => handleRejectTimeLog(log.id)}
                          >
                            Reject
                          </button>
                        </div>
                      ) : (
                        <span className="action-done">-</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Leave Requests Table */}
      {activeTab === 'leaves' && (
        <div className="table-container">
          <table className="attendance-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Leave Type</th>
                <th>Start Date</th>
                <th>End Date</th>
                <th>Total Days</th>
                <th>Reason</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredLeaveRequests.length === 0 ? (
                <tr>
                  <td colSpan="8" className="no-records">No leave requests found</td>
                </tr>
              ) : (
                filteredLeaveRequests.map((leave) => (
                  <tr key={leave.id}>
                    <td>{leave.userName}</td>
                    <td>
                      <span className={`leave-type-badge ${leave.leaveType}`}>
                        {getLeaveTypeLabel(leave.leaveType)}
                      </span>
                    </td>
                    <td>{formatDate(leave.startDate)}</td>
                    <td>{formatDate(leave.endDate)}</td>
                    <td><strong>{leave.totalDays} {leave.totalDays === 1 ? 'day' : 'days'}</strong></td>
                    <td className="desc-cell" title={leave.reason}>{leave.reason}</td>
                    <td>{getStatusBadge(leave.status)}</td>
                    <td>
                      {leave.status === 'pending' ? (
                        <div className="action-buttons">
                          <button
                            className="btn-approve"
                            onClick={() => handleApproveLeave(leave.id)}
                          >
                            Approve
                          </button>
                          <button
                            className="btn-reject"
                            onClick={() => openRejectModal(leave)}
                          >
                            Reject
                          </button>
                        </div>
                      ) : leave.status === 'rejected' && leave.rejectionReason ? (
                        <span className="rejection-reason-badge" title={leave.rejectionReason}>
                          {leave.rejectionReason.length > 20
                            ? leave.rejectionReason.substring(0, 20) + '...'
                            : leave.rejectionReason}
                        </span>
                      ) : (
                        <span className="action-done">-</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Rejection Reason Modal */}
      {showRejectModal && (
        <div className="modal-overlay">
          <div className="modal reject-modal">
            <div className="modal-header">
              <h3>Reject Leave Request</h3>
              <button
                className="modal-close"
                onClick={() => { setShowRejectModal(false); setRejectingLeave(null); setRejectionReason(''); }}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
            <div className="modal-body">
              <p className="reject-info">
                You are rejecting leave request from <strong>{rejectingLeave?.userName}</strong> for{' '}
                <strong>{getLeaveTypeLabel(rejectingLeave?.leaveType)}</strong> ({rejectingLeave?.totalDays} days).
              </p>
              <div className="form-group">
                <label>Rejection Reason *</label>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Please provide a reason for rejecting this leave request..."
                  rows="3"
                  required
                />
              </div>
            </div>
            <div className="modal-actions">
              <button
                className="btn-cancel"
                onClick={() => { setShowRejectModal(false); setRejectingLeave(null); setRejectionReason(''); }}
              >
                Cancel
              </button>
              <button
                className="btn-reject-confirm"
                onClick={handleRejectLeave}
                disabled={!rejectionReason.trim()}
              >
                Reject Leave
              </button>
            </div>
          </div>
        </div>
      )}

      <footer className="admin-footer">
        <p>&copy; {new Date().getFullYear()} AlphaWave Software. All rights reserved.</p>
      </footer>
    </div>
  );
}

export default Admin;
